import React, { useState, useEffect, useRef } from 'react';
import { getTheme } from '../theme';
import { API, DEFAULT_SMS_TEMPLATES, authHeaders, shopHoursText } from '../utils/helpers';
import { getTwilioErrorText } from '../utils/twilioErrorCodes';
import { useTextingReady } from '../utils/texting';
import { paymentTextBody } from '../utils/paymentText';
import TextingOff from './TextingOff';

// A shop's working set, not a full picker. Single code points only: variation
// selectors, skin tones and ZWJ sequences each cost extra characters in a
// UCS-2 message and render inconsistently across phones, for no gain here.
const SMS_EMOJI = [
  '\u{1F44D}','\u{1F44C}','\u2705','\u274C','\u{1F64F}','\u{1F60A}','\u{1F600}','\u{1F389}',
  '\u{1F44F}','\u{1F64C}','\u{1F44B}','\u{1F91D}','\u{1F6B2}','\u{1F527}','\u{1F9F0}','\u{1F529}',
  '\u23F0','\u{1F4C5}','\u{1F4DE}','\u{1F4AC}','\u{1F4F8}','\u{1F4E6}','\u{1F4B0}','\u{1F4B3}',
  '\u{1F9FE}','\u2757','\u2753','\u{1F50B}','\u2728','\u{1F197}','\u{1F605}','\u{1F6A8}',
];

// Real SMS segmentation. The old version assumed 160/320 and capped at 3, which
// understated every long invoice copy — and any emoji at all switches the
// message to UCS-2, where a segment is 70 characters, not 160. A wrong number
// next to a cost estimate is worse than no number.
const GSM7 = "@\u00A3$\u00A5\u00E8\u00E9\u00F9\u00EC\u00F2\u00C7\n\u00D8\u00F8\r\u00C5\u00E5\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039E\u00C6\u00E6\u00DF\u00C9 !\"#\u00A4%&'()*+,-./0123456789:;<=>?\u00A1ABCDEFGHIJKLMNOPQRSTUVWXYZ\u00C4\u00D6\u00D1\u00DC\u00A7\u00BFabcdefghijklmnopqrstuvwxyz\u00E4\u00F6\u00F1\u00FC\u00E0";
const GSM7_EXT = "^{}\\[~]|\u20AC"; // these cost two characters each

function smsInfo(text) {
  const s = text || '';
  let len = 0, gsm = true;
  for (const ch of s) {
    if (GSM7.includes(ch)) len += 1;
    else if (GSM7_EXT.includes(ch)) len += 2;
    else { gsm = false; break; }
  }
  // UCS-2 bills per UTF-16 code unit, which is exactly what .length counts.
  if (!gsm) len = s.length;
  const single = gsm ? 160 : 70;
  const multi  = gsm ? 153 : 67;
  const segments = len === 0 ? 0 : (len <= single ? 1 : Math.ceil(len / multi));
  return { chars: len, segments, encoding: gsm ? 'GSM-7' : 'UCS-2' };
}

const TEMPLATE_BUTTONS = [
  { key: 'pickup',          label: 'Pickup' },
  { key: 'estimate',        label: 'Estimate' },
  { key: 'pickup_reminder', label: 'Pickup Reminder' },
  { key: 'special_order',   label: 'Special Order' },
];

export default function SmsConversation({
  phone, ticketId = null, customerId = null, customerName = '', bikeLabel = '', balance = 0,
  onSent = null, theme = 'dark', markReadOnOpen = false,
  // The ticket's status category from ticket detail ('pickup', 'waiting', ...).
  // Null where the caller does not know it: the payment text then avoids
  // claiming the bike is ready.
  ticketStatusCategory = null,
}) {
  const isDark = theme === 'dark';
  const T = getTheme(theme);
  const textingReady = useTextingReady();

  const [smsTemplates, setSmsTemplates] = useState(DEFAULT_SMS_TEMPLATES);
  const [shopProfile, setShopProfile] = useState({});
  const [smsText, setSmsText] = useState('');
  const [activeTemplateKey, setActiveTemplateKey] = useState(null);
  const [attachInvoice, setAttachInvoice] = useState(false);
  const [includeTechNotes, setIncludeTechNotes] = useState(true);
  const [invoicePending, setInvoicePending] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [smsThread, setSmsThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [openingAttachmentId, setOpeningAttachmentId] = useState(null);
  const [toast, setToast] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null); // { key, thumbKey, mediaType, category, fileName }
  const [uploadingFile, setUploadingFile] = useState(false);
  // Payment link staging. Nothing is created in Stripe until Send — this is
  // only the staged intent-to-request, so abandoning it costs nothing and
  // leaves no orphaned PaymentIntents behind.
  const [payReq, setPayReq] = useState(null);        // { amount, description }
  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payDesc, setPayDesc] = useState('');
  // Sum of payments already collected on this ticket, so the amount field
  // prefills what's actually still owed rather than the full ticket total.
  const [paidToDate, setPaidToDate] = useState(0);
  const threadEndRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const initialScrollDoneRef = useRef(false);
  const fileInputRef = useRef(null);
  const smsInputRef = useRef(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // Booking links for the calendar button. Loaded the first time the button is
  // opened, not on mount: most conversations never need them, and this
  // component mounts for every open thread.
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingLinks, setBookingLinks] = useState(null); // null = not loaded yet
  const [bookingLinksError, setBookingLinksError] = useState(false);
  const MAX_CLIENT_FILE_BYTES = 4.5 * 1024 * 1024;
  const MAX_CLIENT_RAW_VIDEO_BYTES = 30 * 1024 * 1024; // server compresses video down to fit carrier limits — this just guards against absurdly large uploads

  const showToast = (message, ok = true) => {
    setToast({ message, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // getTicketDetail returns '—' for a ticket with no Serialized bike. That's a
  // fine table placeholder but reads as nonsense in a sentence: "your — is
  // ready for pickup". Treat dash placeholders as absent.
  const cleanBike = (s) => {
    const t = (s || '').trim();
    return (t === '—' || t === '-' || t === '–') ? '' : t;
  };

  // THE NUMBER THE CUSTOMER IS TOLD, defined once.
  //
  // Net of payments already collected, so Pickup / Pickup Reminder / Special
  // Order all quote what is actually still owed rather than the original ticket
  // total. It is hoisted out of fillTemplate because the send path records it
  // as well as rendering it -- and a figure stored that differs from the figure
  // sent would be worse than storing nothing, so there is exactly one
  // expression for both.
  const quotedBalanceNet = Math.max(0, (Number(balance) || 0) - paidToDate);
  const quotedBalanceStr = `$${quotedBalanceNet.toFixed(2)}`;

  const fillTemplate = (str) => {
    const firstName = (customerName || '').split(' ')[0] || 'there';
    const bike = cleanBike(bikeLabel) || 'bike';
    const balanceStr = quotedBalanceStr;
    const shopName = shopProfile?.name || 'our shop';
    const shopPhone = shopProfile?.phone || 'the shop';
    const shopHours = shopHoursText(shopProfile) || 'business hours';
    return (str || '')
      .replace(/\{name\}/g, firstName)
      .replace(/\{bike\}/g, bike)
      .replace(/\{balance\}/g, balanceStr)
      .replace(/\{shopName\}/g, shopName)
      .replace(/\{shopPhone\}/g, shopPhone)
      .replace(/\{shopHours\}/g, shopHours);
  };

  // Payment-link message copy: client/src/utils/paymentText.js. "Ready for
  // pickup" only when the ticket's status is in the shop's Pickup category.
  const buildPaymentBody = (amt, desc) => paymentTextBody({
    firstName: (customerName || '').split(' ')[0],
    bike: cleanBike(bikeLabel),
    shopName: shopProfile?.name,
    amount: amt,
    total: remainingBalance,
    paidToDate,
    hasTicket: !!ticketId,
    description: desc,
    readyForPickup: ticketStatusCategory === 'pickup',
  });

  const loadPaidToDate = () => {
    if (!ticketId) { setPaidToDate(0); return; }
    fetch(`${API}/api/payments/list?workorderId=${encodeURIComponent(ticketId)}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${localStorage.getItem('sb_token')}` },
    })
      .then(r => r.ok ? r.json() : { requests: [] })
      .then(d => setPaidToDate((d.requests || [])
        .filter(r => r.status === 'paid')
        .reduce((s, r) => s + Number(r.amount || 0), 0)))
      .catch(() => setPaidToDate(0));
  };

  useEffect(() => { loadPaidToDate(); }, [ticketId]);

  const remainingBalance = Math.max(0, (Number(balance) || 0) - paidToDate);

  const applyTemplate = (key) => {
    const raw = smsTemplates[key] ?? DEFAULT_SMS_TEMPLATES[key] ?? '';
    setSmsText(fillTemplate(raw));
    setActiveTemplateKey(key);
  };

  useEffect(() => {
    fetch(`${API}/api/settings`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(settings => {
        setShopProfile(settings?.shopProfile || {});
        if (settings?.smsTextTemplates) {
          setSmsTemplates({ ...DEFAULT_SMS_TEMPLATES, ...settings.smsTextTemplates });
        }
      })
      .catch(() => {});
  }, []);

  const loadSmsThread = (markRead, silent = false) => {
    if (!phone) return Promise.resolve([]);
    if (!silent) setThreadLoading(true);
    const token = localStorage.getItem('sb_token');
    return fetch(`${API}/api/sms/thread?phone=${encodeURIComponent(phone)}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(async (msgs) => {
        const list = Array.isArray(msgs) ? msgs : [];
        setSmsThread(list);
        if (markRead) {
          await fetch(`${API}/api/sms/read-status`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ phone, read: true }),
          }).catch(() => {});
        }
        return list;
      })
      .catch(err => { console.error('SMS thread fetch failed:', err); return []; })
      .finally(() => { if (!silent) setThreadLoading(false); });
  };

  useEffect(() => {
    setSmsThread([]);
    setSmsText('');
    setActiveTemplateKey(null);
    setAttachInvoice(false);
    setAttachedFile(null);
    setUploadingFile(false);
    setPayReq(null);
    setShowPayForm(false);
    setPayAmount('');
    setPayDesc('');
    lastMessageIdRef.current = null;
    initialScrollDoneRef.current = false;
    if (phone) loadSmsThread(markReadOnOpen);
  }, [phone]);

  // Background poll so an already-open thread picks up new inbound messages
  // in place, without needing to close and reopen it. Silent — never shows
  // the loading state, and never marks read as a side effect (read status
  // stays governed by the explicit open/reply rules elsewhere).
  useEffect(() => {
    if (!phone) return;
    const interval = setInterval(() => { loadSmsThread(false, true); }, 8000);
    return () => clearInterval(interval);
  }, [phone]);

  const pollForNewInvoiceMessage = (countBeforeSend) => {
    setInvoicePending(true);
    let attempts = 0;
    const maxAttempts = 24;
    const tick = async () => {
      attempts++;
      const list = await loadSmsThread(false, true);
      const gotNew = list.length > countBeforeSend;
      if (gotNew || attempts >= maxAttempts) {
        setInvoicePending(false);
        if (!gotNew) showToast('Invoice is taking longer than expected — check back shortly', false);
        return;
      }
      setTimeout(tick, 5000);
    };
    setTimeout(tick, 5000);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const limit = isVideo ? MAX_CLIENT_RAW_VIDEO_BYTES : MAX_CLIENT_FILE_BYTES;
    if (file.size > limit) {
      const limitMb = (limit / (1024 * 1024)).toFixed(0);
      const reason = isVideo
        ? `Video uploads are capped at ${limitMb}MB before compression. Try a shorter clip.`
        : `Files are capped at ${limitMb}MB by the carrier network.`;
      showToast(`"${file.name}" is ${(file.size / (1024 * 1024)).toFixed(1)}MB. ${reason}`, false);
      return;
    }

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('sb_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/sms/upload-attachment`, {
        method: 'POST', credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setAttachedFile({ ...data });
      setAttachInvoice(false); // one attachment per message for now
    } catch (err) {
      showToast(err.message || 'Upload failed', false);
    }
    setUploadingFile(false);
  };

  const openAttachment = async (messageId) => {
    setOpeningAttachmentId(messageId);
    try {
      const token = localStorage.getItem('sb_token');
      const res = await fetch(`${API}/api/sms/${messageId}/media-url`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not load attachment');
      const { url } = await res.json();
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      showToast('Could not open invoice', false);
    }
    setOpeningAttachmentId(null);
  };

  const toggleFollowup = async (msg) => {
    const newVal = !msg.followup;
    setSmsThread(prev => prev.map(m => m.id === msg.id ? { ...m, followup: newVal } : m));
    try {
      const token = localStorage.getItem('sb_token');
      const res = await fetch(`${API}/api/sms/${msg.id}/followup`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ followup: newVal }),
      });
      if (!res.ok) throw new Error('Failed');
    } catch (err) {
      setSmsThread(prev => prev.map(m => m.id === msg.id ? { ...m, followup: !newVal } : m));
      showToast('Could not update follow-up flag', false);
    }
  };

  useEffect(() => {
    if (smsThread.length === 0) return;

    const newestId = smsThread[smsThread.length - 1]?.id;
    const isFirstLoad = !initialScrollDoneRef.current;
    const hasNewMessage = lastMessageIdRef.current !== null && newestId !== lastMessageIdRef.current;

    // Only auto-scroll on:
    //  1. The very first render for this phone (jump to newest on open)
    //  2. A genuinely new message arriving (poll or send)
    // NOT on every poll that returns the same list — that yanks the thread
    // away from the user while they're trying to read history.
    if (isFirstLoad || hasNewMessage) {
      const t1 = setTimeout(() => threadEndRef.current?.scrollIntoView({ block: 'end' }), 50);
      const t2 = setTimeout(() => threadEndRef.current?.scrollIntoView({ block: 'end' }), 350);
      initialScrollDoneRef.current = true;
      lastMessageIdRef.current = newestId;
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }

    lastMessageIdRef.current = newestId;
  }, [smsThread]);

  // Invoice-pending indicator scrolling into view is fine to always trigger
  // — it's a transient bubble the user just requested, so they want to see it.
  useEffect(() => {
    if (invoicePending) {
      setTimeout(() => threadEndRef.current?.scrollIntoView({ block: 'end' }), 50);
    }
  }, [invoicePending]);

  const markReadNow = () => {
    const token = localStorage.getItem('sb_token');
    fetch(`${API}/api/sms/read-status`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ phone, read: true }),
    }).catch(() => {});
  };

  const handleSendSms = async () => {
    if (!phone) return;
    const outgoing = smsText.trim();
    if (!outgoing && !attachInvoice && !attachedFile && !payReq) return;
    setSendingSms(true);
    let finalBody = outgoing;
    try {
      const token = localStorage.getItem('sb_token');

      // Create the payment link first. If this fails we bail before sending
      // anything — a text promising a link that doesn't exist is worse than
      // no text at all. The staged chip stays put so staff can retry.
      if (payReq) {
        const prRes = await fetch(`${API}/api/payments/request`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            lsCustomerId: customerId,
            customerName,
            customerPhone: phone,
            lsWorkorderId: ticketId || null,
            amount: payReq.amount,
            description: payReq.description || null,
            includeTechNotes,
          }),
        });
        const prData = await prRes.json();
        if (!prRes.ok) throw new Error(prData.error || 'Could not create payment link');
        finalBody = outgoing
          ? `${outgoing}\n\n${prData.paymentRequest.url}`
          : `${buildPaymentBody(payReq.amount, payReq.description)}\n\n${prData.paymentRequest.url}`;
      }

      const attachments = attachInvoice ? ['invoice'] : [];
      const res = await fetch(`${API}/api/sms/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          toNumber: phone, message: finalBody, ticketId, customerId, attachments, includeTechNotes,
          // Only when the message being sent still CONTAINS the figure. A
          // template that quoted it and was then hand-edited to remove it
          // records nothing, which is the honest answer -- the server stores
          // null, and the pickup screen says nothing rather than comparing
          // against a number this customer was never given.
          quotedBalance: (finalBody || '').includes(quotedBalanceStr) ? quotedBalanceNet : undefined,
          fileAttachment: attachedFile ? { key: attachedFile.key, thumbKey: attachedFile.thumbKey, mediaType: attachedFile.mediaType } : undefined,
        }),
      });
      if (res.ok) {
        showToast(attachInvoice ? 'Sending message + invoice…' : 'Text message sent!');
        markReadNow();
        if (onSent) onSent('💬 Texted');
        if (attachInvoice) {
          pollForNewInvoiceMessage(smsThread.length);
        } else if (attachedFile) {
          // File sends are synchronous server-side, but the thread row (with
          // its real thumb_url) only exists after a fresh fetch — same
          // reasoning as the earlier invoice-only-send fix: an optimistic
          // bubble here would have no attachment preview to show.
          setTimeout(() => loadSmsThread(false), 1500);
        } else {
          setSmsThread(prev => [...prev, {
            id: Date.now(), direction: 'outbound', body: finalBody,
            created_at: new Date().toISOString(), read: true, followup: false,
          }]);
        }
        setSmsText('');
        setActiveTemplateKey(null);
        setAttachInvoice(false);
        setAttachedFile(null);
        setPayReq(null);
        setShowPayForm(false);
        setPayAmount('');
        setPayDesc('');
        loadPaidToDate();
      }
      else throw new Error('Send failed');
    } catch (err) { showToast(`SMS failed: ${err.message}`, false); }
    setSendingSms(false);
  };

  const border   = isDark ? '#2e3d58' : '#e0e0d8';
  const textPri  = T.text;
  const textSub  = T.textSub;
  const itemBg   = isDark ? '#243050' : '#f0f0e8';
  const inputBg  = isDark ? '#243050' : '#f4f4ef';
  const inputBdr = isDark ? '#2e3d58' : '#d0d0c8';
  const { chars: smsChars, segments: smsSegs, encoding: smsEncoding } = smsInfo(smsText);

  // Insert at the caret, not the end — staff write the message first and drop
  // an emoji mid-sentence more often than after it. React re-renders before
  // the caret can be restored, so put it back on the next frame.
  const insertEmoji = (ch) => {
    const el = smsInputRef.current;
    if (!el) { setSmsText(t => t + ch); return; }
    const start = el.selectionStart ?? smsText.length;
    const end   = el.selectionEnd   ?? smsText.length;
    setSmsText(smsText.slice(0, start) + ch + smsText.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + ch.length;
      el.setSelectionRange(pos, pos);
    });
  };

  // A booking link is a sentence, not a character: when it lands after other
  // words it starts on its own line so the URL is not glued to them.
  const insertBookingText = (text) => {
    const el = smsInputRef.current;
    const start = el ? (el.selectionStart ?? smsText.length) : smsText.length;
    const end   = el ? (el.selectionEnd   ?? smsText.length) : smsText.length;
    const before = smsText.slice(0, start);
    const lead = before && !/\s$/.test(before) ? '\n' : '';
    const piece = lead + text;
    setSmsText(before + piece + smsText.slice(end));
    if (!el) return;
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + piece.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const openBookingLinks = () => {
    setBookingOpen(v => !v);
    if (bookingLinks !== null) return;
    setBookingLinksError(false);
    fetch(`${API}/book/staff/links`, { credentials: 'include', headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setBookingLinks({
        links: Array.isArray(d.links) ? d.links : [],
        generalUrl: d.generalUrl || null,
        generalText: d.generalText || null,
        reason: d.reason || null,
      }))
      .catch(() => setBookingLinksError(true));
  };
  const nothingToSend = !smsText.trim() && !attachInvoice && !attachedFile && !payReq;

  if (!phone) {
    return (
      <div style={{ background:isDark?'#2a1f0a':'#fff8e1', border:`1px solid #e67e22`,
        borderRadius:8, padding:'10px 14px', fontSize:14, color:'#e67e22', lineHeight:1.5 }}>
        No phone number on file. Cannot send SMS.
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, minHeight:0, position:'relative' }}>

      {/* Thread */}
      <div style={{ flex:1, minHeight:0, overflowY:'auto',
        display:'flex', flexDirection:'column', gap:8, padding:'4px 2px', marginBottom:10 }}>
        {threadLoading ? (
          <div style={{ fontSize:13, color:textSub, fontStyle:'italic', textAlign:'center', padding:'16px 0' }}>
            Loading messages…
          </div>
        ) : smsThread.length === 0 ? (
          <div style={{ fontSize:13, color:textSub, fontStyle:'italic', textAlign:'center', padding:'16px 0' }}>
            No messages yet
          </div>
        ) : smsThread.map(msg => {
          const isOut = msg.direction === 'outbound';
          return (
          <div key={msg.id} style={{ display:'flex', flexDirection:'column', gap:4,
            alignItems: isOut ? 'flex-end' : 'flex-start' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, width:'100%',
              justifyContent: isOut ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth:'78%', padding:'8px 12px', fontSize:14, lineHeight:1.4, whiteSpace:'pre-wrap',
                borderRadius: isOut ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                background: isOut ? '#2ecc71' : itemBg,
                color: isOut ? '#fff' : textPri,
                border: msg.followup ? '1px solid #e67e22' : '1px solid transparent' }}>
                <div>{msg.body}</div>
                <div style={{ fontSize:11, marginTop:4, opacity:0.7,
                  textAlign: isOut ? 'right' : 'left' }}>
                  {new Date(msg.created_at).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                </div>
                {isOut && (msg.delivery_status === 'failed' || msg.delivery_status === 'undelivered') && (
                  // A light chip rather than red text straight on the green
                  // bubble, which was hard to read (26 Sep). The raw code is
                  // on hover, for support.
                  <div style={{ marginTop:6, textAlign:'right' }}>
                    <span title={msg.error_code ? `Twilio error ${msg.error_code}` : undefined}
                      style={{ display:'inline-block', fontSize:11, fontWeight:600, lineHeight:1.4,
                        color:'#b3261e', background:'#fff', borderRadius:6, padding:'3px 8px', textAlign:'left' }}>
                      ⚠ Not delivered — {getTwilioErrorText(msg.error_code)}
                    </span>
                  </div>
                )}
              </div>
              {msg.direction === 'inbound' && (
                <button
                  onClick={() => toggleFollowup(msg)}
                  title={msg.followup ? 'Clear follow-up flag' : 'Flag for follow-up'}
                  style={{ background:'transparent', border:'none', cursor:'pointer',
                    fontSize:16, lineHeight:1, padding:'2px 4px', flexShrink:0,
                    color: msg.followup ? '#e67e22' : textSub, opacity: msg.followup ? 1 : 0.5 }}>
                  {msg.followup ? '★' : '☆'}
                </button>
              )}
            </div>

            {/* Attachment card — deliberately not colored like the message
                bubble. The bubble sizes to the text; stretching it to also
                hold a portrait invoice image left a large block of
                unnecessary color around a much narrower image. A separate
                neutral card avoids that. */}
            {msg.thumb_url ? (
              <button
                onClick={() => openAttachment(msg.id)}
                disabled={openingAttachmentId === msg.id}
                title="Click to view full attachment"
                style={{ display:'block', padding:0, width:220, maxWidth:'100%', flexShrink:0,
                  alignSelf: isOut ? 'flex-end' : 'flex-start', boxSizing:'border-box',
                  border:`1px solid ${border}`, borderRadius:8, overflow:'hidden',
                  background:'transparent', cursor: openingAttachmentId === msg.id ? 'wait' : 'pointer',
                  opacity: openingAttachmentId === msg.id ? 0.6 : 1 }}>
                <img src={msg.thumb_url} alt="Attachment"
                  style={{ display:'block', width:'100%', borderRadius:7 }} />
              </button>
            ) : msg.media_key && (
              <button
                onClick={() => openAttachment(msg.id)}
                disabled={openingAttachmentId === msg.id}
                style={{ display:'flex', alignItems:'center', gap:6,
                  padding:'6px 10px', borderRadius:6, cursor: openingAttachmentId === msg.id ? 'wait' : 'pointer',
                  border:`1px solid ${border}`,
                  background: isDark ? '#0d1e2e' : '#eff6ff',
                  color:'#3498db',
                  fontSize:13, fontFamily:"'Inter',sans-serif" }}>
                📎 {openingAttachmentId === msg.id ? 'Opening…' : 'View attachment'}
              </button>
            )}
          </div>
        )})}
        {invoicePending && (
          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <div style={{ maxWidth:'78%', padding:'8px 12px', fontSize:13, fontStyle:'italic',
              borderRadius:'12px 12px 4px 12px', background:itemBg, color:textSub }}>
              🧾 Rendering invoice…
            </div>
          </div>
        )}
        <div ref={threadEndRef} />
      </div>

      {/* Compose area (pinned bottom) */}
      <div style={{ flexShrink:0 }}>

        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
          {TEMPLATE_BUTTONS.map(t => {
            const isActive = activeTemplateKey === t.key;
            return (
              <button key={t.key} onClick={() => applyTemplate(t.key)}
                style={{ padding:'6px 14px', borderRadius:14,
                  border: `1px solid ${isActive ? '#3498db' : border}`,
                  background: isActive ? (isDark ? '#0d1e2e' : '#eff6ff') : 'transparent',
                  color: isActive ? '#3498db' : textSub, fontWeight: isActive ? 600 : 400,
                  fontSize:13, cursor:'pointer',
                  fontFamily:"'Inter',sans-serif", whiteSpace:'nowrap' }}>
                {t.label}
              </button>
            );
          })}
        </div>

        {attachInvoice && (
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8, flexWrap:'wrap' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8,
              padding:'5px 10px', borderRadius:6, background:isDark?'#0d1e2e':'#eff6ff',
              border:'1px solid #3498db' }}>
              <span style={{ fontSize:13, color:'#3498db', fontWeight:600 }}>🧾 Invoice attached</span>
              <button onClick={() => setAttachInvoice(false)}
                title="Remove invoice"
                style={{ background:'transparent', border:'none', color:'#3498db',
                  cursor:'pointer', fontSize:15, lineHeight:1, padding:0 }}>×</button>
            </div>
            {ticketId && (
              <label style={{ display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="checkbox" checked={includeTechNotes}
                  onChange={e => setIncludeTechNotes(e.target.checked)}
                  style={{ accentColor:'#3498db', width:14, height:14, cursor:'pointer' }} />
                <span style={{ fontSize:13, color:textSub }}>Include tech notes</span>
              </label>
            )}
          </div>
        )}

        {attachedFile && (
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:8,
            padding:'5px 10px', borderRadius:6, background:isDark?'#0d1e2e':'#eff6ff',
            border:'1px solid #3498db' }}>
            <span style={{ fontSize:13, color:'#3498db', fontWeight:600 }}>
              📎 {attachedFile.fileName}
            </span>
            <button onClick={() => setAttachedFile(null)}
              title="Remove attachment"
              style={{ background:'transparent', border:'none', color:'#3498db',
                cursor:'pointer', fontSize:15, lineHeight:1, padding:0 }}>×</button>
          </div>
        )}

        {payReq && (
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8, flexWrap:'wrap' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:10,
              padding:'6px 12px', borderRadius:6, background:isDark?'#0d1e2e':'#eff6ff',
              border:'1px solid #3498db' }}>
              <span style={{ fontSize:13, color:'#3498db', fontWeight:600 }}>
                💳 Payment request · ${payReq.amount.toFixed(2)}
              </span>
              {payReq.description && (
                <span style={{ fontSize:12, color:'#3498db', opacity:0.85 }}>{payReq.description}</span>
              )}
              <button onClick={() => setPayReq(null)}
                title="Remove payment request"
                style={{ background:'transparent', border:'none', color:'#3498db',
                  cursor:'pointer', fontSize:15, lineHeight:1, padding:0 }}>×</button>
            </div>
            {/* Same toggle the invoice MMS uses — it governs the receipt note
                on the payment page too. Hidden when the invoice chip is up,
                since that block already renders this checkbox. */}
            {ticketId && !attachInvoice && (
              <label style={{ display:'inline-flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                <input type="checkbox" checked={includeTechNotes}
                  onChange={e => setIncludeTechNotes(e.target.checked)}
                  style={{ accentColor:'#3498db', width:14, height:14, cursor:'pointer' }} />
                <span style={{ fontSize:13, color:textSub }}>Include tech notes</span>
              </label>
            )}
          </div>
        )}

        {showPayForm && !payReq && (
          <div style={{ marginBottom:8, padding:'10px 12px', borderRadius:7,
            background:isDark?'#0d1e2e':'#eff6ff', border:'1px solid #3498db' }}>
            <div style={{ display:'flex', gap:10, marginBottom:8 }}>
              <div style={{ flex:'0 0 130px' }}>
                <div style={{ fontSize:12, color:textSub, marginBottom:4 }}>Amount</div>
                <input type="number" step="0.01" min="0" value={payAmount} autoFocus
                  onChange={e => setPayAmount(e.target.value)}
                  placeholder="0.00"
                  style={{ width:'100%', background:inputBg, border:`1px solid ${inputBdr}`,
                    borderRadius:6, padding:'8px 10px', color:textPri, fontSize:15,
                    fontFamily:"'Inter',sans-serif", outline:'none', boxSizing:'border-box' }} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, color:textSub, marginBottom:4 }}>Description</div>
                <input type="text" value={payDesc}
                  onChange={e => setPayDesc(e.target.value)}
                  placeholder={ticketId ? `Workorder #${ticketId}` : 'What is this for?'}
                  style={{ width:'100%', background:inputBg, border:`1px solid ${inputBdr}`,
                    borderRadius:6, padding:'8px 10px', color:textPri, fontSize:14,
                    fontFamily:"'Inter',sans-serif", outline:'none', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:12, color:textSub }}>
                {ticketId && remainingBalance > 0
                  ? `${paidToDate > 0 ? `Balance due $${remainingBalance.toFixed(2)} of $${Number(balance).toFixed(2)}` : `Ticket total $${remainingBalance.toFixed(2)}`} · `
                  : ''}
                Link is created when you send
              </span>
              <button
                onClick={() => {
                  const amt = Number(payAmount);
                  if (!amt || amt <= 0) { showToast('Enter an amount greater than zero', false); return; }
                  if (amt > 20000) { showToast('Payment links are capped at $20,000.00', false); return; }
                  const desc = payDesc.trim() || (ticketId ? `Workorder #${ticketId}` : '');
                  setPayReq({ amount: amt, description: desc });
                  // Only prefill an empty composer — never clobber something
                  // staff already typed or a template they picked.
                  if (!smsText.trim()) {
                    setSmsText(buildPaymentBody(amt, payDesc.trim()));
                    setActiveTemplateKey(null);
                  }
                  setShowPayForm(false);
                }}
                style={{ marginLeft:'auto', padding:'7px 14px', borderRadius:6,
                  border:'1px solid #3498db', background:'#3498db', color:'#fff',
                  fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'Inter',sans-serif" }}>
                Attach link
              </button>
              <button onClick={() => setShowPayForm(false)}
                style={{ padding:'7px 12px', borderRadius:6, border:`1px solid ${border}`,
                  background:'transparent', color:textSub, fontSize:13, cursor:'pointer',
                  fontFamily:"'Inter',sans-serif" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp,video/mp4,video/quicktime,application/pdf"
          onChange={handleFileSelect}
          style={{ display:'none' }}
        />

        {/* Composer, reorganised 3 Oct: the message box gets the full width
            (it was squeezed to a sliver on a laptop beside six buttons), and
            the tools sit underneath in the order staff reach for them:
            invoice, payment link, booking link, attachment, emoji. The
            emoji and booking pop-ups open from the toolbar's left edge, so
            they can no longer spill past either side of a narrow panel. */}
        {textingReady ? (<>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          <textarea
            ref={smsInputRef}
            value={smsText}
            onChange={e => { setSmsText(e.target.value); }}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleSendSms();
              }
            }}
            rows={4}
            onInput={e => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 200)}px`; }}
            placeholder="Type a message or pick a template…"
            title="Ctrl+Enter to send"
            style={{ width:'100%', minHeight:96, maxHeight:200, overflowY:'auto', background:inputBg, border:`1px solid ${inputBdr}`, borderRadius:7,
              padding:'9px 11px', color:textPri, fontSize:14, fontFamily:"'Inter',sans-serif",
              outline:'none', resize:'none', boxSizing:'border-box', lineHeight:1.5 }}
          />
          {/* position:relative here, not on each button: the emoji and booking
              pop-ups anchor to the toolbar's left edge, so they always have the
              panel's full width whatever button opened them. */}
          <div style={{ position:'relative', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={() => setAttachInvoice(v => !v)}
            disabled={!ticketId || !!attachedFile}
            title={!ticketId ? 'Invoice requires a linked ticket' : attachedFile ? 'Remove the file attachment first' : attachInvoice ? 'Invoice attached — click to remove' : 'Attach full invoice as MMS'}
            style={{ width:42, height:40, borderRadius:7, flexShrink:0,
              border:`1px solid #3498db`, fontSize:18, lineHeight:1,
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor: (ticketId && !attachedFile) ? 'pointer' : 'not-allowed',
              opacity: (ticketId && !attachedFile) ? 1 : 0.4,
              background: attachInvoice ? '#3498db' : (isDark?'#0d1e2e':'#eff6ff'),
              color: attachInvoice ? '#fff' : '#3498db' }}>
            🧾
          </button>
          <button
            onClick={() => {
              if (payReq) { setPayReq(null); return; }
              setPayAmount(ticketId && remainingBalance > 0 ? remainingBalance.toFixed(2) : '');
              setPayDesc('');
              setShowPayForm(v => !v);
            }}
            disabled={!customerId}
            title={!customerId ? 'Payment link requires a matched customer'
              : payReq ? 'Payment request staged — click to remove'
              : 'Request payment via link'}
            style={{ width:42, height:40, borderRadius:7, flexShrink:0,
              border:`1px solid #3498db`, fontSize:17, lineHeight:1,
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor: customerId ? 'pointer' : 'not-allowed',
              opacity: customerId ? 1 : 0.4,
              background: (payReq || showPayForm) ? '#3498db' : (isDark?'#0d1e2e':'#eff6ff'),
              color: (payReq || showPayForm) ? '#fff' : '#3498db' }}>
            💳
          </button>
          {/* Booking link: for a caller who wants to book. Drops the package's
              public booking link into the message, the same link the shop's
              website uses. Works on a thread with no matched customer, which
              is the point -- the caller may never have been in. */}
          <div>
            <button onClick={openBookingLinks} title="Insert a booking link"
              style={{ height:40, width:40, borderRadius:7, cursor:'pointer', fontSize:17,
                background: bookingOpen ? (isDark?'#2e3d58':'#e8e8e0') : 'transparent',
                border:`1px solid ${inputBdr}`, color:textPri, lineHeight:1, padding:0 }}>
              {'\u{1F4C5}'}
            </button>
            {bookingOpen && (
              <>
                <div onClick={() => setBookingOpen(false)} style={{ position:'fixed', inset:0, zIndex:40 }} />
                <div style={{ position:'absolute', bottom:46, left:0, zIndex:41, width:280,
                  maxHeight:320, overflowY:'auto',
                  background: isDark ? '#1a2438' : '#fff', border:`1px solid ${border}`,
                  borderRadius:8, padding:6, boxShadow:'0 8px 24px rgba(0,0,0,0.28)' }}>
                  <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase',
                    color:textSub, padding:'4px 8px 6px' }}>Insert booking link</div>
                  {bookingLinksError ? (
                    <div style={{ fontSize:13, color:textSub, padding:'6px 8px' }}>
                      Could not load booking links. Close this and try again.
                    </div>
                  ) : bookingLinks === null ? (
                    <div style={{ fontSize:13, color:textSub, padding:'6px 8px' }}>Loading…</div>
                  ) : bookingLinks.reason === 'booking_off' ? (
                    <div style={{ fontSize:13, color:textSub, padding:'6px 8px' }}>
                      Online booking is turned off. An administrator can turn it on in Settings, Online Booking.
                    </div>
                  ) : bookingLinks.reason ? (
                    <div style={{ fontSize:13, color:textSub, padding:'6px 8px' }}>
                      This shop has no booking page address yet. Contact Spinboard support.
                    </div>
                  ) : (
                    <>
                      {bookingLinks.links.map(l => (
                        <button key={l.itemID}
                          onClick={() => { insertBookingText(l.text); setBookingOpen(false); }}
                          title={l.url}
                          style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 8px',
                            background:'transparent', border:'none', borderRadius:5, cursor:'pointer',
                            fontSize:13.5, color:textPri, fontFamily:"'Inter',sans-serif" }}>
                          {l.name}
                        </button>
                      ))}
                      {bookingLinks.generalUrl && bookingLinks.generalText && (
                        <button
                          onClick={() => { insertBookingText(bookingLinks.generalText); setBookingOpen(false); }}
                          title={bookingLinks.generalUrl}
                          style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 8px',
                            background:'transparent', border:'none', borderTop:`1px solid ${border}`, borderRadius:0,
                            cursor:'pointer', fontSize:13.5, color:textSub, fontFamily:"'Inter',sans-serif" }}>
                          All services (booking page)
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <button onClick={() => fileInputRef.current?.click()}
            disabled={attachInvoice || uploadingFile}
            title={attachInvoice ? 'Remove the invoice attachment first' : attachedFile ? 'Replace attached file' : 'Attach a photo, video, or PDF'}
            style={{ width:42, height:40, borderRadius:7, flexShrink:0,
              border:`1px solid #3498db`, fontSize:17, lineHeight:1,
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor: (attachInvoice || uploadingFile) ? 'not-allowed' : 'pointer',
              opacity: (attachInvoice || uploadingFile) ? 0.4 : 1,
              background: attachedFile ? '#3498db' : (isDark?'#0d1e2e':'#eff6ff'),
              color: attachedFile ? '#fff' : '#3498db' }}>
            {uploadingFile ? '…' : '📎'}
          </button>
          {/* Curated, not a full picker: a shop reaches for a dozen of these
              and every extra one is a scroll. Watch the counter underneath —
              a single emoji switches the whole message to UCS-2. */}
          <div>
            <button onClick={() => setEmojiOpen(v => !v)} title="Insert emoji"
              style={{ height:40, width:40, borderRadius:7, cursor:'pointer', fontSize:17,
                background: emojiOpen ? (isDark?'#2e3d58':'#e8e8e0') : 'transparent',
                border:`1px solid ${inputBdr}`, color:textPri, lineHeight:1, padding:0 }}>
              {'\u{1F642}'}
            </button>
            {emojiOpen && (
              <>
                {/* Click-away layer, so the popover closes the way every other
                    popover in the app does. */}
                <div onClick={() => setEmojiOpen(false)} style={{ position:'fixed', inset:0, zIndex:40 }} />
                <div style={{ position:'absolute', bottom:46, left:0, zIndex:41, width:244,
                  background: isDark ? '#1a2438' : '#fff', border:`1px solid ${border}`,
                  borderRadius:8, padding:8, boxShadow:'0 8px 24px rgba(0,0,0,0.28)',
                  display:'grid', gridTemplateColumns:'repeat(8, 1fr)', gap:2 }}>
                  {SMS_EMOJI.map(ch => (
                    <button key={ch} onClick={() => { insertEmoji(ch); setEmojiOpen(false); }}
                      title={ch}
                      style={{ fontSize:18, lineHeight:1.4, padding:0, cursor:'pointer',
                        background:'transparent', border:'none', borderRadius:4 }}>
                      {ch}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
            <span style={{ flex:1 }} />
            <button onClick={handleSendSms}
              disabled={sendingSms || nothingToSend}
              style={{ padding:'9px 16px', borderRadius:7, border:'none', fontSize:14, fontWeight:600,
                cursor: sendingSms || nothingToSend ? 'not-allowed' : 'pointer',
                fontFamily:"'Inter',sans-serif", whiteSpace:'nowrap', height:40,
                background: sendingSms || nothingToSend ? (isDark?'#243050':'#d0d0c8') : '#2ecc71',
                color: sendingSms || nothingToSend ? textSub : '#fff' }}>
              {sendingSms ? '…' : 'Send'}
            </button>
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
          <span style={{ fontSize:12, color:smsSegs>1?'#e67e22':textSub }}>
            {smsChars} chars · {smsSegs} SMS segment{smsSegs === 1 ? '' : 's'}
            {smsEncoding === 'UCS-2' && ' · unicode (70/segment)'}
          </span>
          <span style={{ fontSize:12, color:textSub }}>~${(smsSegs * 0.008).toFixed(3)}/send</span>
        </div>
        </>) : <TextingOff theme={theme} compact />}
      </div>

      {toast && (
        <div style={{ position:'absolute', bottom:76, left:'50%', transform:'translateX(-50%)',
          padding:'10px 22px', borderRadius:8, fontSize:14, fontWeight:500,
          boxShadow:'0 4px 16px rgba(0,0,0,0.3)', zIndex:100, whiteSpace:'nowrap',
          background: toast.ok ? '#2ecc71' : '#e74c3c', color:'#fff' }}>
          {toast.ok ? '✓' : '⚠'} {toast.message}
        </div>
      )}
    </div>
  );
}